// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Solenoid synthesis — the coil voices (M3-22, 07 §3.1; the bank reset added by M3-26).
//
// The characteristic pinball sound, and it is three things at once, always:
//
//     transient   2 ms of noise above 3 kHz          the plunger core striking
//     body        a sine falling to 55 Hz            the coil frame resonating in the cabinet
//     mechanism   a bandpassed noise burst at 1.2 kHz   the linkage
//
// Leave out the transient and the coil sounds like a distant thud. Leave out the body and it is
// a click with no cabinet around it. Leave out the mechanism and it is synthetic in a way that
// is hard to name and immediately obvious. The three together are what a player recognises
// without ever having thought about it.
//
// The **pitch drop is the whole trick**. A fixed-pitch body is a beep; the same body sliding
// from 150 Hz to 55 Hz over sixty milliseconds is a thump. It is the coil frame ringing as the
// plunger seats into it, and it is the difference between a pinball machine and a drum machine.
//
// The coils differ only in their numbers, which is the point of a recipe table: a machine
// definition names a coil kind and gets that coil, and adding another is a row rather than a
// function. `bankReset` was the eighth and cost exactly that.

import { Rng } from '@core/rng/Rng'
import {
  addNoiseBurst,
  addPartial,
  addPitchDrop,
  Biquad,
  fadeOut,
  filterInPlace,
  normalise,
  OnePole,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'
import { decibelsToGain } from './webAudio'

export const COIL_VOICES = [
  'flipper',
  'popBumper',
  'slingshot',
  'scoopEject',
  'kickback',
  'troughKick',
  'knocker',
  'bankReset',
] as const

export type CoilVoice = (typeof COIL_VOICES)[number]

export interface SolenoidRecipe {
  /** Where the body starts, Hz. The single number that most defines a coil's character. */
  readonly bodyHz: number
  /** Where it lands. 55 Hz throughout, per 07 §3.1 — the cabinet, not the coil. */
  readonly bodyFloorHz: number
  readonly bodyGlideMs: number
  readonly bodyDecayMs: number
  readonly bodyGain: number
  /** The plunger core striking. Level in dB relative to the body. */
  readonly transientDb: number
  readonly transientMs: number
  readonly mechanismHz: number
  readonly mechanismQ: number
  readonly mechanismDecayMs: number
  /** A lowpass over the finished sound. Only the trough kick, which is under the playfield. */
  readonly muffleHz?: number
  /** The pop bumper's superimposed ring (07 §3.1, cross-referencing §3.2). */
  readonly ringHz?: number
  readonly ringDecayMs?: number
  /** The slingshot's rubber slap: low, soft, and gone almost immediately. */
  readonly slapHz?: number
  readonly slapDecayMs?: number
}

const BODY_FLOOR_HZ = 55
const BODY_GLIDE_MS = 60
const TRANSIENT_MS = 2
const TRANSIENT_HIGHPASS_HZ = 3000

/**
 * The table from 07 §3.1, plus the character notes beside it.
 *
 * Body pitch and decay are the doc's; everything else is what "tight", "fatter", "sharpest",
 * "deep", "a real bang" and "muffled" mean in numbers.
 */
export const SOLENOID_RECIPES: Readonly<Record<CoilVoice, SolenoidRecipe>> = {
  // Tight and snappy. The most frequent sound in the machine by an enormous margin — a player
  // hears it several times a second — so it is the one that must not become tiring.
  flipper: {
    bodyHz: 150,
    bodyFloorHz: BODY_FLOOR_HZ,
    bodyGlideMs: 45,
    bodyDecayMs: 70,
    bodyGain: 0.75,
    transientDb: -6,
    transientMs: TRANSIENT_MS,
    mechanismHz: 1400,
    mechanismQ: 3,
    mechanismDecayMs: 28,
  },
  // Fatter, longer, with the ring on top. A pop bumper is the loudest thing on the playfield
  // that is not a kickback, and the ring is what makes a bumper storm exciting rather than
  // repetitive.
  popBumper: {
    bodyHz: 110,
    bodyFloorHz: BODY_FLOOR_HZ,
    bodyGlideMs: BODY_GLIDE_MS,
    bodyDecayMs: 110,
    bodyGain: 0.9,
    transientDb: -5,
    transientMs: TRANSIENT_MS,
    mechanismHz: 1200,
    mechanismQ: 3,
    mechanismDecayMs: 40,
    ringHz: 1320,
    ringDecayMs: 130,
  },
  // The sharpest of all: it fires beside the flippers, where the player is looking hardest, so
  // it has to read as an event without lasting long enough to mask the shot that follows.
  slingshot: {
    bodyHz: 175,
    bodyFloorHz: BODY_FLOOR_HZ,
    bodyGlideMs: 38,
    bodyDecayMs: 55,
    bodyGain: 0.72,
    transientDb: -3,
    transientMs: TRANSIENT_MS,
    mechanismHz: 1600,
    mechanismQ: 3.5,
    mechanismDecayMs: 24,
    slapHz: 320,
    slapDecayMs: 34,
  },
  // Deep. The ball rattle that follows it in 07 §3.1 is a *ball* sound and belongs to M3-24.
  scoopEject: {
    bodyHz: 95,
    bodyFloorHz: BODY_FLOOR_HZ,
    bodyGlideMs: 70,
    bodyDecayMs: 140,
    bodyGain: 0.95,
    transientDb: -7,
    transientMs: TRANSIENT_MS,
    mechanismHz: 1100,
    mechanismQ: 2.6,
    mechanismDecayMs: 48,
  },
  // The loudest playfield coil, and a real bang. It saves a ball, so it should sound like it.
  kickback: {
    bodyHz: 88,
    bodyFloorHz: BODY_FLOOR_HZ,
    bodyGlideMs: 75,
    bodyDecayMs: 160,
    bodyGain: 1,
    transientDb: -2,
    transientMs: 2.5,
    mechanismHz: 1000,
    mechanismQ: 2.4,
    mechanismDecayMs: 55,
  },
  // Muffled, because it is under the playfield with a sheet of wood between it and the player.
  // The lowpass is the wood.
  troughKick: {
    bodyHz: 105,
    bodyFloorHz: BODY_FLOOR_HZ,
    bodyGlideMs: BODY_GLIDE_MS,
    bodyDecayMs: 120,
    bodyGain: 0.8,
    transientDb: -12,
    transientMs: TRANSIENT_MS,
    mechanismHz: 900,
    mechanismQ: 2.2,
    mechanismDecayMs: 45,
    muffleHz: 900,
  },
  // The drop-target bank's reset coil: a big under-playfield solenoid driving a common bar, so
  // it is heavy and it is heard through the playfield rather than across it. Named in the
  // format's own synthesis vocabulary (`SYNTH_NAMES`) as `bankReset`, which is why it is a coil
  // rather than an approximation borrowed from one of the others — a table exists so that the
  // eighth is a row.
  bankReset: {
    bodyHz: 92,
    bodyFloorHz: BODY_FLOOR_HZ,
    bodyGlideMs: 68,
    bodyDecayMs: 130,
    bodyGain: 0.95,
    transientDb: -6,
    transientMs: TRANSIENT_MS,
    mechanismHz: 1050,
    mechanismQ: 2.5,
    mechanismDecayMs: 50,
    muffleHz: 1600,
  },
  // Deliberately startling. A knocker is a free game announcing itself by hitting the inside of
  // the cabinet next to your head, and a polite one is a knocker that has failed.
  knocker: {
    bodyHz: 70,
    bodyFloorHz: 48,
    bodyGlideMs: 90,
    bodyDecayMs: 220,
    bodyGain: 1,
    transientDb: 0,
    transientMs: 3,
    mechanismHz: 780,
    mechanismQ: 1.8,
    mechanismDecayMs: 90,
  },
}

/**
 * The flipper's return stroke: the same coil de-energising, with much less of the strike.
 *
 * 07 §3.1 asks for it by name, and it matters more than it sounds. A flipper that makes one
 * noise per press is a button; a flipper that makes a different, softer noise when it drops is
 * a mechanism, and the player stops hearing a control and starts hearing a machine.
 */
export const FLIPPER_RETURN: SolenoidRecipe = {
  ...SOLENOID_RECIPES.flipper,
  bodyHz: 128,
  bodyDecayMs: 52,
  bodyGain: 0.42,
  transientDb: -16,
  mechanismDecayMs: 20,
}

/** Rng, as a noise source. Its own stream — 07 rule 4 keeps the physics generator for physics. */
export function noiseFrom(rng: Rng): NoiseSource {
  return { next: () => rng.nextFloat() * 2 - 1 }
}

/** How long a recipe needs to decay away completely. */
export function solenoidLengthMs(recipe: SolenoidRecipe): number {
  const longest = Math.max(
    recipe.bodyDecayMs,
    recipe.mechanismDecayMs,
    recipe.ringDecayMs ?? 0,
    recipe.slapDecayMs ?? 0,
  )
  // 1.15× the −60 dB time, which puts the truncation at about −69 dB, under a 2 ms fade, under
  // a machine that is also playing music. It was 1.6× and that tail is pure cost: rendering is
  // proportional to samples, the buffers are the longest in the product, and 07 §9 budgets the
  // *whole* machine at 40 ms on the low tier. Measured in the browser, the change takes the
  // coil bank from 12.6 seconds of audio to 9.2 — nothing audible, a quarter off the load.
  return longest * 1.15 + 8
}

/**
 * Render one coil hit.
 *
 * Normalised at the end, so the *mix* decides how loud a coil is and the recipe decides only
 * what it sounds like. Without that, tuning the mechanism filter silently re-balances the
 * machine and the person who did it was adjusting a timbre.
 */
export function renderSolenoid(
  recipe: SolenoidRecipe,
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(solenoidLengthMs(recipe), sampleRate))

  // The coil frame. First, because it is the loudest and longest part and everything else is
  // measured against it.
  addPitchDrop(
    out,
    sampleRate,
    recipe.bodyHz * pitchScale,
    recipe.bodyFloorHz * pitchScale,
    recipe.bodyGlideMs,
    recipe.bodyGain,
    recipe.bodyDecayMs,
  )

  // The plunger core striking: two milliseconds of noise with everything below 3 kHz taken out.
  // Short enough that it reads as an attack rather than as a sound of its own.
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.highpass(TRANSIENT_HIGHPASS_HZ, 0.7, sampleRate),
    decibelsToGain(recipe.transientDb) * recipe.bodyGain,
    recipe.transientMs,
  )

  // The linkage.
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(recipe.mechanismHz * pitchScale, recipe.mechanismQ, sampleRate),
    0.5 * recipe.bodyGain,
    recipe.mechanismDecayMs,
  )

  if (recipe.ringHz !== undefined) {
    addPartial(out, sampleRate, recipe.ringHz * pitchScale, 0.22, recipe.ringDecayMs ?? 120)
  }
  if (recipe.slapHz !== undefined) {
    addPartial(out, sampleRate, recipe.slapHz * pitchScale, 0.3, recipe.slapDecayMs ?? 30)
  }

  if (recipe.muffleHz !== undefined) {
    filterInPlace(out, new OnePole(recipe.muffleHz, sampleRate))
  }

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}
